import Cookies from 'js-cookie'
import { isServer } from '@vue-storefront/core/helpers'

export const VARIANT_COOKIE = 'ab_variant'

const COOKIE_PATH = '/'
const COOKIE_EXPIRY_DAYS = 365
const DEFAULT_ROUTE = 'checkout'

/**
 * The variant decides a route, not analytics, so this cookie is strictly
 * necessary and deliberately not consent-gated.
 */
export const writeVariant = (variant: string): void => {
  if (isServer || !variant) {
    return
  }

  Cookies.set(VARIANT_COOKIE, variant, {
    expires: COOKIE_EXPIRY_DAYS,
    path: COOKIE_PATH,
    sameSite: 'Lax',
    secure: true
  })
}

/**
 * Releasing the letter is what makes the admin switch a real off switch: the
 * cookie outlives the experiment by a year, and routing reads it before
 * anything else.
 */
export const clearVariant = (): void => {
  if (isServer) {
    return
  }

  Cookies.remove(VARIANT_COOKIE, { path: COOKIE_PATH })
}

export const readVariant = (): string | null => {
  if (isServer) {
    return null
  }

  return Cookies.get(VARIANT_COOKIE) || null
}

export const resolveRoute = (variant: string | null, routes: Record<string, string>): string => {
  const code = String(variant || '').trim().toUpperCase()

  if (Object.prototype.hasOwnProperty.call(routes, code)) {
    return routes[code]
  }

  return Object.values(routes)[0] || DEFAULT_ROUTE
}

/**
 * A letter with no configured route would be served as the default variant but
 * reported as itself, so every customer holding it would be counted under a
 * checkout they never saw.
 */
export const isMappedVariant = (variant: string | null, routes: Record<string, string>): boolean => {
  const code = String(variant || '').trim().toUpperCase()

  return code !== '' && Object.prototype.hasOwnProperty.call(routes || {}, code)
}

/**
 * True when the variant is one of those configured to see checkout without the full footer.
 */
export const isReducedFooterVariant = (variant: string | null, reducedVariants?: string[]): boolean => {
  const code = String(variant || '').trim().toUpperCase()

  return code !== '' && (reducedVariants || []).includes(code)
}

/**
 * True when the path is any configured checkout route, in any store view.
 */
export const isCheckoutPath = (routePath: string, routes: Record<string, string>): boolean => {
  const segments = String(routePath || '').split('/').filter(Boolean)
  const last = segments[segments.length - 1]

  return !!last && Object.values(routes).includes(last)
}
